<template>
  <section class="cb-page">
    <template v-if="schoolId">
      <header class="cb-toolbar">
        <div class="cb-toolbar__left">
          <h1 class="cb-toolbar__title" :title="schoolName ? `为 ${schoolName} 按校区划分教学楼栋` : '按校区划分教学楼栋'">
            班级楼栋分区
          </h1>
          <span class="cb-toolbar__divider" aria-hidden="true"></span>
          <label class="cb-toolbar__field">
            <span class="cb-toolbar__label">校区</span>
            <el-select
              v-model="campusId"
              filterable
              placeholder="请选择校区"
              class="cb-campus-select"
              :disabled="!campusOptions.length"
              @change="onCampusChange"
            >
              <el-option
                v-for="item in campusOptions"
                :key="String(item.Id != null ? item.Id : item.id)"
                :label="item.Name || item.name"
                :value="String(item.Id != null ? item.Id : item.id)"
              />
            </el-select>
          </label>
        </div>
        <div class="cb-toolbar__right">
          <div class="cb-kpi" title="已建楼栋数">
            <b>{{ buildings.length }}</b>
            <span>楼栋</span>
          </div>
          <div class="cb-kpi" title="已划分到楼栋的班级数">
            <b>{{ assignedCount }}</b>
            <span>已划分</span>
          </div>
          <div class="cb-kpi cb-kpi--warn" title="尚未划分楼栋的班级数">
            <b>{{ unassignedClasses.length }}</b>
            <span>未分配</span>
          </div>
          <span class="cb-toolbar__divider" aria-hidden="true"></span>
          <button
            type="button"
            class="cb-btn cb-btn--primary"
            :disabled="!canOperate"
            @click="openBuildingDialog()"
          >
            <i class="el-icon-plus"></i>
            新增楼栋
          </button>
          <button
            type="button"
            class="cb-btn cb-btn--ghost"
            :disabled="loading || !canOperate"
            @click="loadOverview"
          >
            <i class="el-icon-refresh" :class="{ 'is-loading': loading }"></i>
            刷新
          </button>
        </div>
      </header>

      <div v-if="requireCampus && !hasCampusSelected" class="cb-empty">
        <div class="cb-empty__visual" aria-hidden="true">
          <span class="cb-empty__block"></span>
          <span class="cb-empty__block"></span>
          <span class="cb-empty__block"></span>
        </div>
        <h2>请先选择校区</h2>
        <p>当前学校有多个校区，请先选择校区后再维护楼栋与班级。</p>
      </div>

      <div v-else v-loading="loading" class="cb-workspace">
        <aside class="cb-rail">
          <div class="cb-rail__sticky">
            <div class="cb-rail__label">分区导航</div>
            <button
              type="button"
              class="cb-nav-item cb-nav-item--unassigned"
              :class="{ 'is-active': viewMode === 'unassigned' }"
              @click="selectUnassigned"
            >
              <span class="cb-nav-item__icon"><i class="el-icon-box"></i></span>
              <span class="cb-nav-item__body">
                <span class="cb-nav-item__name">未分配</span>
                <span class="cb-nav-item__meta">待划分班级</span>
              </span>
              <span class="cb-nav-item__badge">{{ unassignedClasses.length }}</span>
            </button>
            <div class="cb-rail__divider">已建楼栋</div>
          </div>

          <div class="cb-rail__scroll">
            <div v-if="!buildings.length" class="cb-rail__empty">
              还没有楼栋，点击右上角新增
            </div>

            <div
              v-for="(building, bIndex) in buildings"
              :key="building.Id || building.id"
              class="cb-nav-item"
              :class="{ 'is-active': viewMode === 'building' && String(activeBuildingId) === String(pickId(building)) }"
              @click="selectBuilding(building)"
            >
              <span class="cb-nav-item__index">{{ bIndex + 1 }}</span>
              <span class="cb-nav-item__body">
                <span class="cb-nav-item__name">{{ pickName(building) }}</span>
                <span class="cb-nav-item__meta">{{ classCount(building) }} 个班</span>
              </span>
              <span class="cb-nav-item__sort" @click.stop>
                <button
                  type="button"
                  class="cb-icon-btn"
                  title="上移"
                  :disabled="bIndex === 0"
                  @click="moveBuilding(bIndex, -1)"
                >
                  <i class="el-icon-arrow-up"></i>
                </button>
                <button
                  type="button"
                  class="cb-icon-btn"
                  title="下移"
                  :disabled="bIndex === buildings.length - 1"
                  @click="moveBuilding(bIndex, 1)"
                >
                  <i class="el-icon-arrow-down"></i>
                </button>
              </span>
            </div>
          </div>
        </aside>

        <main class="cb-panel">
          <!-- 空楼栋引导 -->
          <div v-if="viewMode === 'empty'" class="cb-empty">
            <div class="cb-empty__visual" aria-hidden="true">
              <span class="cb-empty__block"></span>
              <span class="cb-empty__block"></span>
              <span class="cb-empty__block"></span>
            </div>
            <h2>先建一个楼栋分区</h2>
            <p>例如「西区南楼」「中东区北楼」。建好后再把班级划进去并排序。</p>
            <button type="button" class="cb-btn cb-btn--primary" @click="openBuildingDialog()">
              <i class="el-icon-plus"></i>
              新增第一个楼栋
            </button>
          </div>

          <!-- 未分配 -->
          <template v-else-if="viewMode === 'unassigned'">
            <div class="cb-panel__head">
              <div>
                <h2 class="cb-panel__title">未分配班级</h2>
                <p class="cb-panel__sub">共 {{ filteredUnassigned.length }} / {{ unassignedClasses.length }} 个班 · 按年级分组展示</p>
              </div>
              <div class="cb-panel__tools">
                <el-input
                  v-model="filterKeyword"
                  size="small"
                  clearable
                  prefix-icon="el-icon-search"
                  placeholder="搜索年级或班级"
                  class="cb-search"
                />
                <el-select
                  v-if="buildings.length"
                  v-model="quickAssignBuildingId"
                  size="small"
                  clearable
                  placeholder="划入楼栋"
                  class="cb-quick-select"
                >
                  <el-option
                    v-for="b in buildings"
                    :key="pickId(b)"
                    :label="pickName(b)"
                    :value="String(pickId(b))"
                  />
                </el-select>
                <button
                  v-if="buildings.length"
                  type="button"
                  class="cb-btn cb-btn--primary cb-btn--sm"
                  :disabled="!selectedUnassignedIds.length || !quickAssignBuildingId"
                  @click="quickAssignSelected"
                >
                  划入所选 ({{ selectedUnassignedIds.length }})
                </button>
              </div>
            </div>

            <div class="cb-panel__scroll">
              <div v-if="!unassignedClasses.length" class="cb-panel__done">
                <i class="el-icon-circle-check"></i>
                全部班级已划分到楼栋
              </div>

              <div v-else class="cb-grade-blocks">
                <section v-for="group in unassignedGroups" :key="group.grade" class="cb-grade">
                  <div class="cb-grade__head">
                    <span class="cb-grade__name">{{ group.grade }}</span>
                    <span class="cb-grade__count">{{ group.items.length }}</span>
                    <button type="button" class="cb-link" @click="toggleGradeSelection(group)">
                      {{ isGradeFullySelected(group) ? '取消全选' : '全选' }}
                    </button>
                  </div>
                  <div class="cb-chip-grid">
                    <label
                      v-for="row in group.items"
                      :key="pickDeptId(row)"
                      class="cb-chip"
                      :class="{ 'is-checked': selectedUnassignedIds.indexOf(String(pickDeptId(row))) >= 0 }"
                    >
                      <input
                        type="checkbox"
                        class="cb-chip__check"
                        :value="String(pickDeptId(row))"
                        :checked="selectedUnassignedIds.indexOf(String(pickDeptId(row))) >= 0"
                        @change="toggleUnassigned(row, $event)"
                      />
                      <span class="cb-chip__text">{{ pickClassName(row) }}</span>
                    </label>
                  </div>
                </section>
                <div v-if="!unassignedGroups.length" class="cb-panel__done">没有匹配的班级</div>
              </div>
            </div>
          </template>

          <!-- 某个楼栋 -->
          <template v-else-if="activeBuilding">
            <div class="cb-panel__head">
              <div>
                <h2 class="cb-panel__title">{{ pickName(activeBuilding) }}</h2>
                <p class="cb-panel__sub">
                  修改序号后点「保存排序」生效 · 共 {{ activeClasses.length }} 个班
                  <span v-if="classOrderDirty" class="cb-panel__dirty">（有未保存修改）</span>
                </p>
              </div>
              <div class="cb-panel__tools">
                <button
                  type="button"
                  class="cb-btn cb-btn--primary cb-btn--sm"
                  :disabled="!classOrderDirty || savingClassOrder"
                  @click="saveClassOrder"
                >
                  <i v-if="savingClassOrder" class="el-icon-loading"></i>
                  保存排序
                </button>
                <button
                  type="button"
                  class="cb-btn cb-btn--ghost cb-btn--sm"
                  :disabled="!activeClasses.length || savingClassOrder"
                  title="按学段、年级、班级重置为教学架构顺序"
                  @click="resetClassOrderByTeaching"
                >
                  教学架构排序
                </button>
                <button type="button" class="cb-btn cb-btn--ghost cb-btn--sm" @click="openAssignDialog(activeBuilding)">
                  <i class="el-icon-connection"></i>
                  划分班级
                </button>
                <button type="button" class="cb-btn cb-btn--ghost cb-btn--sm" @click="openBuildingDialog(activeBuilding)">
                  改名
                </button>
                <button type="button" class="cb-btn cb-btn--danger-ghost cb-btn--sm" @click="handleDeleteBuilding(activeBuilding)">
                  删除
                </button>
              </div>
            </div>

            <div class="cb-panel__scroll">
              <div v-if="!activeClasses.length" class="cb-panel__done cb-panel__done--dashed">
                <p>该楼栋还没有班级</p>
                <button type="button" class="cb-btn cb-btn--primary cb-btn--sm" @click="openAssignDialog(activeBuilding)">
                  去划分
                </button>
              </div>

              <ol v-else class="cb-order-list">
                <li
                  v-for="row in activeClasses"
                  :key="pickDeptId(row)"
                  class="cb-order-item"
                  :class="{ 'is-dirty': isClassOrderChanged(row) }"
                >
                  <div class="cb-order-item__sort" @click.stop>
                    <el-input-number
                      :value="pickOrderSort(row)"
                      :min="1"
                      :max="9999"
                      :controls="false"
                      size="small"
                      class="cb-order-input"
                      @change="val => onClassOrderChange(activeBuilding, row, val)"
                    />
                  </div>
                  <div class="cb-order-item__body">
                    <span class="cb-order-item__class">{{ pickClassName(row) }}</span>
                    <span class="cb-order-item__grade">{{ pickGradeName(row) || '未识别年级' }}</span>
                  </div>
                  <div class="cb-order-item__ops">
                    <button
                      type="button"
                      class="cb-icon-btn cb-icon-btn--danger"
                      title="移出"
                      @click="handleRemoveClass(activeBuilding, row)"
                    >
                      <i class="el-icon-close"></i>
                    </button>
                  </div>
                </li>
              </ol>
            </div>
          </template>
        </main>
      </div>
    </template>

    <div v-else class="cb-empty cb-empty--page">
      <div class="cb-empty__visual" aria-hidden="true">
        <span class="cb-empty__block"></span>
        <span class="cb-empty__block"></span>
        <span class="cb-empty__block"></span>
      </div>
      <h2>请先选择学校</h2>
      <p>在顶部学校下拉中切换后，即可维护该校的楼栋分区。</p>
    </div>

    <el-dialog
      :title="buildingForm.Id ? '修改楼栋' : '新增楼栋'"
      :visible.sync="buildingDialogVisible"
      width="440px"
      custom-class="cb-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form ref="buildingForm" :model="buildingForm" :rules="buildingRules" label-width="88px" size="small">
        <el-form-item label="楼栋名称" prop="Name">
          <el-input
            v-model="buildingForm.Name"
            maxlength="100"
            placeholder="如：西区南楼、中东区北楼"
            @keyup.enter.native="submitBuilding"
          />
        </el-form-item>
        <el-form-item label="楼栋序号" prop="OrderSort">
          <el-input-number
            v-model="buildingForm.OrderSort"
            :min="1"
            :max="9999"
            :step="1"
            controls-position="right"
            style="width: 160px;"
          />
          <div class="cb-form-tip">数字越小越靠前，左侧分区导航按此排序</div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="buildingDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="saving" @click="submitBuilding">确定</el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="划分班级"
      :visible.sync="assignDialogVisible"
      width="720px"
      custom-class="cb-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <div class="cb-assign-head">
        <div>
          目标楼栋：<strong>{{ assignBuildingName }}</strong>
        </div>
        <el-input
          v-model="assignKeyword"
          size="small"
          clearable
          prefix-icon="el-icon-search"
          placeholder="筛选年级或班级"
          class="cb-search"
        />
      </div>
      <el-table
        ref="assignTable"
        :data="filteredAssignOptions"
        size="small"
        border
        height="420"
        class="cb-assign-table"
        highlight-current-row
        @row-click="onAssignRowClick"
        @selection-change="onAssignSelectionChange"
      >
        <el-table-column type="selection" width="45" />
        <el-table-column label="年级" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">{{ pickGradeName(scope.row) || '-' }}</template>
        </el-table-column>
        <el-table-column label="班级" min-width="160" show-overflow-tooltip>
          <template slot-scope="scope">{{ pickClassName(scope.row) || '-' }}</template>
        </el-table-column>
        <el-table-column label="当前归属" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.fromBuildingName" class="cb-tag cb-tag--muted">{{ scope.row.fromBuildingName }}</span>
            <span v-else class="cb-tag cb-tag--warn">未分配</span>
          </template>
        </el-table-column>
      </el-table>
      <div slot="footer">
        <span class="cb-assign-count">已选 {{ assignSelection.length }} 个班</span>
        <el-button size="small" @click="assignDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="saving" @click="submitAssign">确定划分</el-button>
      </div>
    </el-dialog>
  </section>
</template>

<script>
import { resolveActiveSchoolId, normalizeDeptId } from '../../utils/deptCascaderProps';
import {
  getClassBuildingCampusOptions,
  getClassBuildingOverview,
  saveClassBuilding,
  deleteClassBuilding,
  saveClassBuildingSort,
  assignClassBuildingClasses,
  removeClassBuildingClasses,
  saveClassBuildingClassSort
} from '../../api/modules/classBuilding';

function pick(row, pascal, camel, fallback) {
  if (!row) return fallback;
  if (row[pascal] !== undefined && row[pascal] !== null) return row[pascal];
  if (row[camel] !== undefined && row[camel] !== null) return row[camel];
  return fallback;
}

export default {
  name: 'ClassBuildingIndex',
  data() {
    return {
      schoolId: '',
      schoolName: '',
      campusId: '',
      campusOptions: [],
      requireCampus: false,
      loading: false,
      saving: false,
      buildings: [],
      unassignedClasses: [],
      viewMode: 'empty',
      activeBuildingId: '',
      filterKeyword: '',
      selectedUnassignedIds: [],
      quickAssignBuildingId: '',
      buildingDialogVisible: false,
      buildingForm: { Id: 0, Name: '', OrderSort: 1 },
      buildingRules: {
        Name: [{ required: true, message: '请输入楼栋名称', trigger: 'blur' }],
        OrderSort: [{ required: true, message: '请填写楼栋序号', trigger: 'change' }]
      },
      assignDialogVisible: false,
      assignBuildingId: 0,
      assignBuildingName: '',
      assignOptions: [],
      assignSelection: [],
      assignKeyword: '',
      /** 当前楼栋已保存的班级序号快照，用于判断是否有未保存修改 */
      classOrderSnapshot: '',
      savingClassOrder: false
    };
  },
  computed: {
    hasCampusSelected() {
      if (!this.requireCampus) return true;
      return this.campusId !== '' && this.campusId != null;
    },
    canOperate() {
      return !!this.schoolId && this.hasCampusSelected;
    },
    resolvedCampusId() {
      if (!this.requireCampus) return '0';
      return this.campusId === '' || this.campusId == null ? '' : String(this.campusId);
    },
    assignedCount() {
      return (this.buildings || []).reduce((sum, b) => sum + this.classCount(b), 0);
    },
    activeBuilding() {
      if (this.viewMode !== 'building') return null;
      return (this.buildings || []).find(b => String(this.pickId(b)) === String(this.activeBuildingId)) || null;
    },
    activeClasses() {
      if (!this.activeBuilding) return [];
      return pick(this.activeBuilding, 'Classes', 'classes', []) || [];
    },
    classOrderDirty() {
      if (!this.activeBuilding) return false;
      return this.buildClassOrderSnapshot(this.activeBuilding) !== this.classOrderSnapshot;
    },
    filteredUnassigned() {
      const kw = (this.filterKeyword || '').trim().toLowerCase();
      const list = (this.unassignedClasses || []).slice();
      // 学段 → 年级排序 → 班级排序（与教学架构 GradeOrderNo / ClassOrderNo 一致）
      list.sort((a, b) => this.compareClassRow(a, b));
      if (!kw) return list;
      return list.filter(row => {
        const text = `${this.pickGradeName(row)} ${this.pickClassName(row)}`.toLowerCase();
        return text.indexOf(kw) >= 0;
      });
    },
    unassignedGroups() {
      const map = {};
      const order = [];
      (this.filteredUnassigned || []).forEach(row => {
        const grade = this.pickGradeName(row) || '未分组';
        if (!map[grade]) {
          map[grade] = [];
          order.push(grade);
        }
        map[grade].push(row);
      });
      return order.map(grade => ({
        grade,
        items: map[grade].slice().sort((a, b) => this.compareClassOrderOnly(a, b))
      }));
    },
    filteredAssignOptions() {
      const kw = (this.assignKeyword || '').trim().toLowerCase();
      if (!kw) return this.assignOptions || [];
      return (this.assignOptions || []).filter(row => {
        const text = `${this.pickGradeName(row)} ${this.pickClassName(row)} ${row.fromBuildingName || ''}`.toLowerCase();
        return text.indexOf(kw) >= 0;
      });
    }
  },
  created() {
    this.applySchoolChange(resolveActiveSchoolId(this), this.$store.state.activeSchoolName);
  },
  watch: {
    '$store.state.activeSchoolId'(val) {
      this.applySchoolChange(val, this.$store.state.activeSchoolName);
    }
  },
  methods: {
    pickId(row) {
      return pick(row, 'Id', 'id', 0);
    },
    pickName(row) {
      return pick(row, 'Name', 'name', '') || '';
    },
    pickDeptId(row) {
      return pick(row, 'DepartmentId', 'departmentId', 0);
    },
    pickClassName(row) {
      return pick(row, 'ClassName', 'className', '') || '';
    },
    pickGradeName(row) {
      return pick(row, 'GradeName', 'gradeName', '') || '';
    },
    pickOrderSort(row) {
      const n = Number(pick(row, 'OrderSort', 'orderSort', 1));
      return Number.isFinite(n) && n > 0 ? n : 1;
    },
    setOrderSort(row, value) {
      const next = Math.max(1, Number(value) || 1);
      if (Object.prototype.hasOwnProperty.call(row, 'OrderSort') || row.OrderSort !== undefined) {
        row.OrderSort = next;
      } else {
        row.orderSort = next;
      }
      return next;
    },
    /** 该班序号相对快照是否已改动（未保存） */
    isClassOrderChanged(row) {
      if (!row || !this.classOrderSnapshot) return false;
      const id = String(this.pickDeptId(row));
      const parts = this.classOrderSnapshot.split('|');
      for (let i = 0; i < parts.length; i++) {
        const seg = parts[i];
        if (!seg) continue;
        const colon = seg.indexOf(':');
        if (colon < 0) continue;
        if (seg.slice(0, colon) !== id) continue;
        const saved = Number(seg.slice(colon + 1));
        return this.pickOrderSort(row) !== saved;
      }
      return false;
    },
    pickSortNum(row, pascal, camel, fallback) {
      const n = Number(pick(row, pascal, camel, fallback));
      return Number.isFinite(n) ? n : 2147483647;
    },
    /** 学段 → 年级 → 班级排序 */
    compareClassRow(a, b) {
      const pa = this.pickSortNum(a, 'PeriodOrder', 'periodOrder', 2147483647);
      const pb = this.pickSortNum(b, 'PeriodOrder', 'periodOrder', 2147483647);
      if (pa !== pb) return pa - pb;
      const ga = this.pickSortNum(a, 'GradeOrder', 'gradeOrder', 2147483647);
      const gb = this.pickSortNum(b, 'GradeOrder', 'gradeOrder', 2147483647);
      if (ga !== gb) return ga - gb;
      const nameA = this.pickGradeName(a);
      const nameB = this.pickGradeName(b);
      if (nameA !== nameB) return nameA.localeCompare(nameB, 'zh');
      return this.compareClassOrderOnly(a, b);
    },
    /** 仅按班级排序（ClassOrderNo） */
    compareClassOrderOnly(a, b) {
      const ca = this.pickSortNum(a, 'ClassOrder', 'classOrder', 2147483647);
      const cb = this.pickSortNum(b, 'ClassOrder', 'classOrder', 2147483647);
      if (ca !== cb) return ca - cb;
      const na = this.pickClassName(a);
      const nb = this.pickClassName(b);
      if (na !== nb) return na.localeCompare(nb, 'zh');
      return Number(this.pickDeptId(a)) - Number(this.pickDeptId(b));
    },
    classCount(building) {
      return (pick(building, 'Classes', 'classes', []) || []).length;
    },

    applySchoolChange(schoolId, schoolName) {
      const id = normalizeDeptId(schoolId);
      this.schoolId = id;
      this.schoolName = schoolName || '';
      this.campusId = '';
      this.campusOptions = [];
      this.requireCampus = false;
      this.selectedUnassignedIds = [];
      this.quickAssignBuildingId = '';
      this.filterKeyword = '';
      this.buildings = [];
      this.unassignedClasses = [];
      this.viewMode = 'empty';
      this.activeBuildingId = '';
      if (!id) return;
      this.loadCampusesAndOverview();
    },

    async loadCampusesAndOverview() {
      if (!this.schoolId) return;
      this.loading = true;
      try {
        const res = await getClassBuildingCampusOptions({ schoolId: this.schoolId });
        if (!res.data || !res.data.success) {
          this.$message.error((res.data && res.data.msg) || '加载校区失败');
          return;
        }
        const list = res.data.response || [];
        this.campusOptions = list;
        this.requireCampus = list.some(item => Number(pick(item, 'Id', 'id', 0)) > 0);
        if (!this.requireCampus) {
          this.campusId = '0';
        } else if (!this.campusId) {
          const first = list.find(item => Number(pick(item, 'Id', 'id', 0)) > 0);
          this.campusId = first ? String(pick(first, 'Id', 'id', '')) : '';
        }
        if (this.hasCampusSelected) {
          await this.loadOverview(false);
        }
      } catch (e) {
        this.$message.error('加载校区失败');
      } finally {
        this.loading = false;
      }
    },

    onCampusChange() {
      this.selectedUnassignedIds = [];
      this.quickAssignBuildingId = '';
      this.filterKeyword = '';
      this.activeBuildingId = '';
      this.loadOverview();
    },

    syncViewAfterLoad() {
      if (!this.buildings.length) {
        this.viewMode = this.unassignedClasses.length ? 'unassigned' : 'empty';
        this.activeBuildingId = '';
        return;
      }
      if (this.viewMode === 'building' && this.activeBuildingId) {
        const still = this.buildings.some(b => String(this.pickId(b)) === String(this.activeBuildingId));
        if (still) return;
      }
      if (this.unassignedClasses.length) {
        this.viewMode = 'unassigned';
        this.activeBuildingId = '';
        return;
      }
      this.selectBuilding(this.buildings[0]);
    },

    selectUnassigned() {
      const go = () => {
        this.viewMode = 'unassigned';
        this.activeBuildingId = '';
        this.classOrderSnapshot = '';
      };
      if (this.classOrderDirty) {
        this.$confirm('当前楼栋序号尚未保存，是否放弃修改？', '提示', { type: 'warning' })
          .then(async () => {
            await this.loadOverview(false);
            go();
          })
          .catch(() => {});
        return;
      }
      go();
    },

    selectBuilding(building) {
      const targetId = String(this.pickId(building) || '');
      const go = () => {
        this.viewMode = 'building';
        this.activeBuildingId = targetId;
        const current = (this.buildings || []).find(b => String(this.pickId(b)) === targetId) || building;
        this.classOrderSnapshot = this.buildClassOrderSnapshot(current);
      };
      if (this.classOrderDirty && String(this.activeBuildingId) !== targetId) {
        this.$confirm('当前楼栋序号尚未保存，是否放弃修改？', '提示', { type: 'warning' })
          .then(async () => {
            await this.loadOverview(false);
            go();
          })
          .catch(() => {});
        return;
      }
      go();
    },

    /** 生成楼栋内班级序号快照（部门Id:序号） */
    buildClassOrderSnapshot(building) {
      const classes = pick(building, 'Classes', 'classes', []) || [];
      return classes
        .map(c => `${this.pickDeptId(c)}:${this.pickOrderSort(c)}`)
        .join('|');
    },

    async loadOverview(showLoading = true) {
      if (!this.schoolId || !this.hasCampusSelected) return;
      if (showLoading) this.loading = true;
      try {
        const res = await getClassBuildingOverview({
          schoolId: this.schoolId,
          campusId: this.resolvedCampusId || 0
        });
        if (!res.data || !res.data.success) {
          this.$message.error((res.data && res.data.msg) || '加载失败');
          return;
        }
        const data = res.data.response || {};
        const campusList = data.Campuses || data.campuses;
        if (campusList && campusList.length) {
          this.campusOptions = campusList;
          this.requireCampus = campusList.some(item => Number(pick(item, 'Id', 'id', 0)) > 0);
        }
        this.buildings = data.Buildings || data.buildings || [];
        this.unassignedClasses = data.UnassignedClasses || data.unassignedClasses || [];
        this.selectedUnassignedIds = [];
        this.syncViewAfterLoad();
        if (this.activeBuilding) {
          this.classOrderSnapshot = this.buildClassOrderSnapshot(this.activeBuilding);
        } else {
          this.classOrderSnapshot = '';
        }
      } catch (e) {
        this.$message.error('加载失败');
      } finally {
        if (showLoading) this.loading = false;
      }
    },

    nextBuildingOrderSort() {
      const list = this.buildings || [];
      if (!list.length) return 1;
      let max = 0;
      list.forEach(b => {
        const sort = Number(pick(b, 'OrderSort', 'orderSort', 0)) || 0;
        if (sort > max) max = sort;
      });
      return max + 1;
    },

    openBuildingDialog(building) {
      this.buildingForm = {
        Id: building ? (this.pickId(building) || 0) : 0,
        Name: building ? this.pickName(building) : '',
        OrderSort: building
          ? (Number(pick(building, 'OrderSort', 'orderSort', 1)) || 1)
          : this.nextBuildingOrderSort()
      };
      this.buildingDialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.buildingForm) this.$refs.buildingForm.clearValidate();
      });
    },

    submitBuilding() {
      this.$refs.buildingForm.validate(async (valid) => {
        if (!valid) return;
        this.saving = true;
        try {
          const res = await saveClassBuilding({
            Id: this.buildingForm.Id,
            SchoolId: this.schoolId,
            CampusId: this.resolvedCampusId || 0,
            Name: (this.buildingForm.Name || '').trim(),
            OrderSort: Number(this.buildingForm.OrderSort) || 1
          });
          if (!res.data || !res.data.success) {
            this.$message.error((res.data && res.data.msg) || '保存失败');
            return;
          }
          this.$message.success((res.data && res.data.msg) || '保存成功');
          this.buildingDialogVisible = false;
          const savedId = res.data.response;
          await this.loadOverview();
          if (savedId && !this.buildingForm.Id) {
            const created = this.buildings.find(b => String(this.pickId(b)) === String(savedId));
            if (created) this.selectBuilding(created);
          }
        } catch (e) {
          this.$message.error('保存失败');
        } finally {
          this.saving = false;
        }
      });
    },

    handleDeleteBuilding(building) {
      const id = this.pickId(building);
      const name = this.pickName(building);
      if (this.classCount(building) > 0) {
        this.$message.warning('请先移出该楼栋下的班级，再删除');
        return;
      }
      this.$confirm(`确认删除楼栋「${name}」？`, '提示', { type: 'warning' })
        .then(async () => {
          const res = await deleteClassBuilding({
            schoolId: this.schoolId,
            campusId: this.resolvedCampusId || 0,
            buildingId: id
          });
          if (!res.data || !res.data.success) {
            this.$message.error((res.data && res.data.msg) || '删除失败');
            return;
          }
          this.$message.success((res.data && res.data.msg) || '删除成功');
          this.activeBuildingId = '';
          await this.loadOverview();
        })
        .catch(() => {});
    },

    async moveBuilding(index, delta) {
      const target = index + delta;
      if (target < 0 || target >= this.buildings.length) return;
      const list = this.buildings.slice();
      const tmp = list[index];
      list[index] = list[target];
      list[target] = tmp;
      this.buildings = list;
      const buildingIds = list.map(b => this.pickId(b)).filter(Boolean);
      try {
        const res = await saveClassBuildingSort({
          SchoolId: this.schoolId,
          CampusId: this.resolvedCampusId || 0,
          BuildingIds: buildingIds
        });
        if (!res.data || !res.data.success) {
          this.$message.error((res.data && res.data.msg) || '排序保存失败');
          await this.loadOverview();
        }
      } catch (e) {
        this.$message.error('排序保存失败');
        await this.loadOverview();
      }
    },

    /**
     * 修改楼栋内班级序号：只改数字、不立刻重排，点「保存排序」后再排序并提交
     */
    onClassOrderChange(building, row, val) {
      if (!building || !row) return;
      this.setOrderSort(row, val);
      this.buildings = this.buildings.slice();
    },

    /**
     * 保存楼栋内班级排序（按当前填写的序号）
     */
    async saveClassOrder() {
      const building = this.activeBuilding;
      if (!building || !this.classOrderDirty) return;
      const classes = (pick(building, 'Classes', 'classes', []) || []).slice();
      classes.sort((a, b) => {
        const sa = this.pickOrderSort(a);
        const sb = this.pickOrderSort(b);
        if (sa !== sb) return sa - sb;
        return Number(this.pickDeptId(a)) - Number(this.pickDeptId(b));
      });
      await this.persistClassOrder(building, classes);
    },

    /**
     * 按教学架构（学段→年级→班级）重置楼栋内顺序并保存
     */
    async resetClassOrderByTeaching() {
      const building = this.activeBuilding;
      if (!building) return;
      const classes = (pick(building, 'Classes', 'classes', []) || []).slice();
      if (!classes.length) return;
      classes.sort((a, b) => this.compareClassRow(a, b));
      await this.persistClassOrder(building, classes, '已按教学架构排序');
    },

    /**
     * 按传入列表顺序写回 1..n 并提交保存
     */
    async persistClassOrder(building, classes, successMsg) {
      if (!building || !classes || !classes.length) return;
      const list = classes.slice();
      list.forEach((c, i) => this.setOrderSort(c, i + 1));
      if (building.Classes) building.Classes = list;
      else building.classes = list;
      this.buildings = this.buildings.slice();

      this.savingClassOrder = true;
      try {
        const res = await saveClassBuildingClassSort({
          SchoolId: this.schoolId,
          BuildingId: this.pickId(building),
          DepartmentIds: list.map(c => this.pickDeptId(c)).filter(Boolean)
        });
        if (!res.data || !res.data.success) {
          this.$message.error((res.data && res.data.msg) || '排序保存失败');
          await this.loadOverview();
          return;
        }
        this.$message.success(successMsg || (res.data && res.data.msg) || '排序已保存');
        this.classOrderSnapshot = this.buildClassOrderSnapshot(building);
      } catch (e) {
        this.$message.error('排序保存失败');
        await this.loadOverview();
      } finally {
        this.savingClassOrder = false;
      }
    },

    handleRemoveClass(building, row) {
      const buildingId = this.pickId(building);
      const departmentId = this.pickDeptId(row);
      const className = this.pickClassName(row);
      this.$confirm(`确认将「${className}」移出当前楼栋？`, '提示', { type: 'warning' })
        .then(async () => {
          const res = await removeClassBuildingClasses({
            SchoolId: this.schoolId,
            BuildingId: buildingId,
            DepartmentIds: [departmentId]
          });
          if (!res.data || !res.data.success) {
            this.$message.error((res.data && res.data.msg) || '移出失败');
            return;
          }
          this.$message.success((res.data && res.data.msg) || '已移出');
          await this.loadOverview();
          if (buildingId) {
            const still = this.buildings.find(b => String(this.pickId(b)) === String(buildingId));
            if (still) this.selectBuilding(still);
          }
        })
        .catch(() => {});
    },

    toggleUnassigned(row, event) {
      const id = String(this.pickDeptId(row));
      const checked = event && event.target ? event.target.checked : false;
      const next = this.selectedUnassignedIds.slice();
      const idx = next.indexOf(id);
      if (checked && idx < 0) next.push(id);
      if (!checked && idx >= 0) next.splice(idx, 1);
      this.selectedUnassignedIds = next;
    },

    isGradeFullySelected(group) {
      if (!group || !group.items || !group.items.length) return false;
      return group.items.every(row => this.selectedUnassignedIds.indexOf(String(this.pickDeptId(row))) >= 0);
    },

    toggleGradeSelection(group) {
      const ids = (group.items || []).map(row => String(this.pickDeptId(row)));
      if (this.isGradeFullySelected(group)) {
        this.selectedUnassignedIds = this.selectedUnassignedIds.filter(id => ids.indexOf(id) < 0);
      } else {
        const set = {};
        this.selectedUnassignedIds.forEach(id => { set[id] = true; });
        ids.forEach(id => { set[id] = true; });
        this.selectedUnassignedIds = Object.keys(set);
      }
    },

    async quickAssignSelected() {
      if (!this.quickAssignBuildingId || !this.selectedUnassignedIds.length) return;
      this.saving = true;
      try {
        const res = await assignClassBuildingClasses({
          SchoolId: this.schoolId,
          CampusId: this.resolvedCampusId || 0,
          BuildingId: this.quickAssignBuildingId,
          DepartmentIds: this.selectedUnassignedIds.slice()
        });
        if (!res.data || !res.data.success) {
          this.$message.error((res.data && res.data.msg) || '划分失败');
          return;
        }
        this.$message.success((res.data && res.data.msg) || '划分成功');
        const targetId = this.quickAssignBuildingId;
        await this.loadOverview();
        const building = this.buildings.find(b => String(this.pickId(b)) === String(targetId));
        if (building) this.selectBuilding(building);
      } catch (e) {
        this.$message.error('划分失败');
      } finally {
        this.saving = false;
      }
    },

    openAssignDialog(building) {
      const buildingId = this.pickId(building);
      const buildingName = this.pickName(building);
      this.assignBuildingId = buildingId;
      this.assignBuildingName = buildingName;
      this.assignSelection = [];
      this.assignKeyword = '';

      const options = [];
      (this.unassignedClasses || []).forEach(c => {
        options.push({
          DepartmentId: this.pickDeptId(c),
          ClassName: this.pickClassName(c),
          GradeName: this.pickGradeName(c),
          PeriodOrder: this.pickSortNum(c, 'PeriodOrder', 'periodOrder', 2147483647),
          GradeOrder: this.pickSortNum(c, 'GradeOrder', 'gradeOrder', 2147483647),
          ClassOrder: this.pickSortNum(c, 'ClassOrder', 'classOrder', 2147483647),
          fromBuildingName: ''
        });
      });
      (this.buildings || []).forEach(b => {
        const bid = this.pickId(b);
        if (String(bid) === String(buildingId)) return;
        const bname = this.pickName(b);
        (pick(b, 'Classes', 'classes', []) || []).forEach(c => {
          options.push({
            DepartmentId: this.pickDeptId(c),
            ClassName: this.pickClassName(c),
            GradeName: this.pickGradeName(c),
            PeriodOrder: this.pickSortNum(c, 'PeriodOrder', 'periodOrder', 2147483647),
            GradeOrder: this.pickSortNum(c, 'GradeOrder', 'gradeOrder', 2147483647),
            ClassOrder: this.pickSortNum(c, 'ClassOrder', 'classOrder', 2147483647),
            fromBuildingName: bname
          });
        });
      });
      options.sort((a, b) => this.compareClassRow(a, b));
      this.assignOptions = options;
      this.assignDialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.assignTable) this.$refs.assignTable.clearSelection();
      });
    },

    onAssignSelectionChange(rows) {
      this.assignSelection = rows || [];
    },

    /** 点击整行切换选中（点复选框本身时不重复切换） */
    onAssignRowClick(row, column, event) {
      const target = event && event.target;
      if (target && (target.closest('.el-checkbox') || target.closest('input'))) return;
      if (column && column.type === 'selection') return;
      if (this.$refs.assignTable) this.$refs.assignTable.toggleRowSelection(row);
    },

    async submitAssign() {
      if (!this.assignSelection.length) {
        this.$message.warning('请先勾选班级');
        return;
      }
      this.saving = true;
      try {
        const departmentIds = this.assignSelection
          .map(r => this.pickDeptId(r))
          .filter(Boolean);
        const res = await assignClassBuildingClasses({
          SchoolId: this.schoolId,
          CampusId: this.resolvedCampusId || 0,
          BuildingId: this.assignBuildingId,
          DepartmentIds: departmentIds
        });
        if (!res.data || !res.data.success) {
          this.$message.error((res.data && res.data.msg) || '划分失败');
          return;
        }
        this.$message.success((res.data && res.data.msg) || '划分成功');
        this.assignDialogVisible = false;
        const targetId = this.assignBuildingId;
        await this.loadOverview();
        const building = this.buildings.find(b => String(this.pickId(b)) === String(targetId));
        if (building) this.selectBuilding(building);
      } catch (e) {
        this.$message.error('划分失败');
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.cb-page {
  --cb-bg: #f5f7fa;
  --cb-ink: #303133;
  --cb-muted: #909399;
  --cb-line: #e4e7ed;
  --cb-primary: #409eff;
  --cb-primary-soft: #ecf5ff;
  --cb-accent: #337ecc;
  --cb-warn: #e6a23c;
  --cb-warn-soft: #fdf6ec;
  --cb-card: #ffffff;
  --cb-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  --cb-radius: 10px;
  --cb-font: 'PingFang SC', 'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif;
  --cb-display: var(--cb-font);

  height: calc(100vh - 118px);
  max-height: calc(100vh - 118px);
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  background: var(--cb-bg);
  color: var(--cb-ink);
  font-family: var(--cb-font);
}

/* 顶部单行工具条：标题 + 校区 + KPI + 操作 */
.cb-toolbar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px 20px;
  flex-wrap: wrap;
  min-height: 56px;
  padding: 12px 16px;
  margin-bottom: 12px;
  border: 1px solid var(--cb-line);
  border-radius: 10px;
  background: var(--cb-card);
  box-shadow: var(--cb-shadow);
}

.cb-toolbar__left,
.cb-toolbar__right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
}

.cb-toolbar__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.01em;
  white-space: nowrap;
}

.cb-toolbar__divider {
  width: 1px;
  height: 28px;
  background: var(--cb-line);
  flex-shrink: 0;
}

.cb-toolbar__field {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
}

.cb-toolbar__label {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  white-space: nowrap;
}

.cb-campus-select {
  width: 220px;
}

.cb-kpi {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  min-width: 64px;
  height: 44px;
  padding: 4px 12px;
  box-sizing: border-box;
  border-radius: 8px;
  background: #f5f7fa;
  border: 1px solid var(--cb-line);
  white-space: nowrap;
  text-align: center;
  line-height: 1;
}

.cb-kpi b {
  display: block;
  width: 100%;
  font-size: 18px;
  font-weight: 700;
  color: var(--cb-primary);
  font-variant-numeric: tabular-nums;
  text-align: center;
  line-height: 1.15;
}

.cb-kpi span {
  display: block;
  width: 100%;
  margin-top: 2px;
  font-size: 12px;
  color: var(--cb-muted);
  text-align: center;
  line-height: 1.15;
}

.cb-kpi--warn {
  background: var(--cb-warn-soft);
  border-color: #faecd8;
}

.cb-kpi--warn b {
  color: var(--cb-warn);
}

.cb-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  font-family: inherit;
  white-space: nowrap;
}

.cb-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.cb-btn--sm {
  height: 32px;
  padding: 0 12px;
  font-size: 12px;
}

.cb-btn--primary {
  background: var(--cb-primary);
  color: #fff;
  box-shadow: none;
}

.cb-btn--primary:hover:not(:disabled) {
  background: var(--cb-accent);
}

.cb-btn--ghost {
  background: #fff;
  border-color: var(--cb-line);
  color: var(--cb-ink);
}

.cb-btn--ghost:hover:not(:disabled) {
  border-color: var(--cb-primary);
  color: var(--cb-primary);
}

.cb-btn--danger-ghost {
  background: #fff;
  border-color: #fecaca;
  color: #dc2626;
}

.cb-btn--danger-ghost:hover:not(:disabled) {
  background: #fef2f2;
}

.cb-workspace {
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: 255px minmax(0, 1fr);
  gap: 12px;
  overflow: hidden;
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.cb-rail,
.cb-panel {
  min-height: 0;
  height: 100%;
  background: var(--cb-card);
  border: 1px solid var(--cb-line);
  border-radius: var(--cb-radius);
  box-shadow: var(--cb-shadow);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.cb-rail {
  /* 覆盖全局 .container .main aside 的 flex:0 0 210px */
  flex: 0 0 255px !important;
  width: 255px !important;
  max-width: 255px;
  padding: 12px 10px;
  box-sizing: border-box;
}

.cb-rail__sticky {
  flex: 0 0 auto;
}

.cb-rail__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-right: 2px;
  -webkit-overflow-scrolling: touch;
}

.cb-rail__label,
.cb-rail__divider {
  padding: 0 8px;
  margin-bottom: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7f969c;
}

.cb-rail__divider {
  margin-top: 14px;
}

.cb-rail__empty {
  padding: 14px 10px;
  margin: 0 4px;
  border-radius: 12px;
  background: #f8fafb;
  color: var(--cb-muted);
  font-size: 12px;
  line-height: 1.5;
}

.cb-nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 10px;
  margin-bottom: 6px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
  font-family: inherit;
  color: inherit;
}

.cb-nav-item:hover {
  background: #f5f7fa;
  border-color: var(--cb-line);
}

.cb-nav-item.is-active {
  background: var(--cb-primary-soft);
  border-color: #d9ecff;
  box-shadow: inset 3px 0 0 var(--cb-primary);
}

.cb-nav-item--unassigned.is-active {
  background: var(--cb-warn-soft);
  border-color: #faecd8;
  box-shadow: inset 3px 0 0 var(--cb-warn);
}

.cb-nav-item__icon,
.cb-nav-item__index {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f0f2f5;
  color: #606266;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.cb-nav-item.is-active .cb-nav-item__icon,
.cb-nav-item.is-active .cb-nav-item__index {
  background: #d9ecff;
  color: var(--cb-primary);
}

.cb-nav-item--unassigned .cb-nav-item__icon {
  background: #f5f7fa;
  color: var(--cb-warn);
}

.cb-nav-item--unassigned.is-active .cb-nav-item__icon {
  background: #faecd8;
  color: var(--cb-warn);
}

.cb-nav-item__body {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cb-nav-item__name {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cb-nav-item__meta {
  font-size: 11px;
  color: var(--cb-muted);
}

.cb-nav-item__badge {
  min-width: 28px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: var(--cb-warn);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cb-nav-item__sort {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cb-icon-btn {
  width: 24px;
  height: 22px;
  border: 1px solid var(--cb-line);
  border-radius: 6px;
  background: #fff;
  color: var(--cb-muted);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
  padding: 0;
}

.cb-icon-btn:hover:not(:disabled) {
  color: var(--cb-primary);
  border-color: var(--cb-primary);
}

.cb-icon-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.cb-icon-btn--danger:hover:not(:disabled) {
  color: #dc2626;
  border-color: #fca5a5;
  background: #fef2f2;
}

.cb-panel {
  padding: 16px 20px 12px;
}

.cb-panel__head {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.cb-panel__scroll {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-right: 4px;
  padding-bottom: 8px;
  -webkit-overflow-scrolling: touch;
}

.cb-panel__title {
  margin: 0;
  font-family: var(--cb-display);
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.cb-panel__sub {
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--cb-muted);
}

.cb-panel__dirty {
  color: var(--cb-warn);
  font-weight: 600;
}

.cb-panel__tools {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.cb-search {
  width: 200px;
}

.cb-quick-select {
  width: 150px;
}

.cb-panel__done {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 140px;
  width: 100%;
  padding: 28px 16px;
  box-sizing: border-box;
  border-radius: 10px;
  border: 1px solid var(--cb-line);
  background: #fafafa;
  color: var(--cb-muted);
  font-size: 14px;
}

.cb-panel__done--dashed {
  border-style: dashed;
  background: #fff;
}

.cb-panel__done i {
  font-size: 28px;
  color: var(--cb-primary);
}

.cb-grade-blocks {
  flex: 0 0 auto;
}

.cb-grade-blocks {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.cb-grade {
  padding: 12px 14px 14px;
  border-radius: 8px;
  background: #fafafa;
  border: 1px solid var(--cb-line);
}

.cb-grade__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.cb-grade__name {
  font-size: 13px;
  font-weight: 700;
}

.cb-grade__count {
  min-width: 22px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e9e9eb;
  color: #606266;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cb-link {
  margin-left: auto;
  border: 0;
  background: transparent;
  color: var(--cb-primary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
}

.cb-link:hover {
  text-decoration: underline;
}

.cb-chip-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(118px, 1fr));
  gap: 8px;
}

.cb-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid #dbe7ea;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
  margin: 0;
}

.cb-chip:hover {
  border-color: #c0c4cc;
}

.cb-chip.is-checked {
  border-color: #a0cfff;
  background: var(--cb-primary-soft);
  box-shadow: none;
}

.cb-chip__check {
  margin: 0;
  accent-color: var(--cb-primary);
}

.cb-chip__text {
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--cb-ink);
}

.cb-order-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 10px;
  width: 100%;
}

.cb-order-item {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  min-width: 0;
  height: 64px;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--cb-line);
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.cb-order-item:hover {
  border-color: #c0c4cc;
  box-shadow: none;
}

.cb-order-item.is-dirty {
  background: var(--cb-warn-soft);
  border-color: #f5dab1;
}

.cb-order-item.is-dirty:hover {
  border-color: var(--cb-warn);
}

.cb-order-item__sort {
  width: 56px;
}

.cb-order-input {
  width: 56px;
}

.cb-order-input >>> .el-input__inner {
  padding-left: 8px;
  padding-right: 8px;
  text-align: center;
  font-weight: 700;
  font-family: var(--cb-display);
  color: var(--cb-primary);
  background: var(--cb-primary-soft);
  border-color: #b3d8ff;
}

.cb-order-input >>> .el-input__inner:hover,
.cb-order-input >>> .el-input__inner:focus {
  border-color: var(--cb-primary);
  background: #d9ecff;
}

.cb-order-item.is-dirty .cb-order-input >>> .el-input__inner {
  color: #b88230;
  background: #faecd8;
  border-color: #f5dab1;
}

.cb-order-item.is-dirty .cb-order-input >>> .el-input__inner:hover,
.cb-order-item.is-dirty .cb-order-input >>> .el-input__inner:focus {
  border-color: var(--cb-warn);
  background: #f8e3c5;
}

.cb-order-item__body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cb-order-item__class {
  font-size: 14px;
  font-weight: 650;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cb-order-item__grade {
  font-size: 12px;
  color: var(--cb-muted);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cb-order-item__ops {
  display: flex;
  gap: 6px;
}

.cb-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 420px;
  padding: 24px;
  gap: 8px;
}

.cb-empty--page {
  min-height: 70vh;
}

.cb-empty h2 {
  margin: 8px 0 0;
  font-family: var(--cb-display);
  font-size: 22px;
}

.cb-empty p {
  margin: 0 0 8px;
  max-width: 360px;
  color: var(--cb-muted);
  font-size: 13px;
  line-height: 1.6;
}

.cb-empty__visual {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  height: 64px;
}

.cb-empty__block {
  width: 28px;
  border-radius: 8px 8px 4px 4px;
  background: #c0c4cc;
  opacity: 0.9;
  animation: cb-rise 1.6s ease-in-out infinite;
}

.cb-empty__block:nth-child(2) {
  background: var(--cb-primary);
  opacity: 0.75;
}

.cb-empty__block:nth-child(1) { height: 28px; animation-delay: 0s; }
.cb-empty__block:nth-child(2) { height: 48px; animation-delay: 0.15s; }
.cb-empty__block:nth-child(3) { height: 36px; animation-delay: 0.3s; }

@keyframes cb-rise {
  0%, 100% { transform: translateY(0); opacity: 0.7; }
  50% { transform: translateY(-6px); opacity: 1; }
}

.cb-assign-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 13px;
  color: var(--cb-muted);
}

.cb-assign-head strong {
  color: var(--cb-ink);
}

.cb-assign-count {
  margin-right: auto;
  font-size: 12px;
  color: var(--cb-muted);
}

.cb-tag {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.cb-tag--muted {
  background: #eef2f3;
  color: #547078;
}

.cb-tag--warn {
  background: var(--cb-warn-soft);
  color: var(--cb-warn);
}

.cb-form-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

@media (max-width: 1100px) {
  .cb-page {
    height: auto;
    max-height: none;
    overflow: visible;
  }

  .cb-toolbar {
    align-items: flex-start;
  }

  .cb-toolbar__right {
    width: 100%;
    justify-content: flex-start;
  }

  .cb-workspace {
    grid-template-columns: 1fr;
    min-height: 0;
    overflow: visible;
  }

  .cb-rail {
    max-height: 260px;
  }

  .cb-panel {
    min-height: 0;
    height: auto;
  }

  .cb-panel__done {
    min-height: 120px;
  }

  .cb-panel__scroll,
  .cb-rail__scroll {
    max-height: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cb-empty__block,
  .cb-btn,
  .cb-nav-item,
  .cb-chip,
  .cb-order-item,
  .cb-icon-btn {
    animation: none !important;
    transition: none !important;
  }
}
</style>

<style>
/* 覆盖全局 home.css: .container .main aside { flex: 0 0 210px } */
.container .main .cb-page aside.cb-rail {
  flex: 0 0 255px;
  width: 255px;
  max-width: 255px;
}

.cb-dialog .el-dialog__header {
  padding: 16px 20px 8px;
}

.cb-dialog .el-dialog__title {
  font-weight: 700;
  color: #12333a;
}

.cb-dialog .el-dialog__body {
  padding: 8px 20px 12px;
}

.cb-assign-table >>> .el-table__body tr {
  cursor: pointer;
}

.cb-assign-table >>> .el-table__body td {
  height: 44px;
  padding-top: 0;
  padding-bottom: 0;
}

.cb-assign-table >>> .el-table__header th {
  height: 40px;
  padding-top: 0;
  padding-bottom: 0;
}

.cb-assign-table >>> .cb-tag {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  height: 24px;
  line-height: 24px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.cb-assign-table th {
  background: #f5f7fa !important;
  color: #909399;
  font-weight: 600;
}
</style>
